import type { CalendarEventContextMenuViewState } from '../store/schema/CalendarEventContextMenuViewState';
import getStore from '../store/store';
import { observer } from 'mobx-react-lite';
import type { ViewType } from 'owa-calendar-actions';
import type { CalendarEvent } from 'owa-calendar-types';
import type { ClientItemId } from 'owa-client-ids';
import { CalendarItemContextMenu } from 'owa-event-context-menu';
import { lazyPopoutCalendarCompose } from 'owa-popout-calendar';
import { useOrchestrator } from 'owa-react-hooks/lib/useOrchestrator';
import type EventScope from 'owa-service/lib/contract/EventScope';
import * as React from 'react';
import {
    getInitializedEvent,
    getSourceId,
    isContextMenuOpen,
} from '../selectors/calendarEventContextMenuStoreSelectors';
import {
    cancelMeeting,
    deleteEvent,
    editEvent,
    forwardEvent,
    proposeNewTime,
    replyToMeeting,
    respondToMeeting,
    verifyEventData,
    closeEventContextMenu,
    openEventDetails,
    openEventContextMenu,
    duplicateEvent,
} from '../actions/calendarEventContextMenuStoreActions';

export default observer(function CalendarEventContextMenu({
    sourceId,
    viewState,
    onCloseContextMenu,
    onOpenEventDetails,
    onEditEvent,
}: {
    sourceId: ViewType;
    viewState: CalendarEventContextMenuViewState | null;
    onCloseContextMenu?: () => void;
    onOpenEventDetails?: (id: ClientItemId) => void;
    onEditEvent?: (event: CalendarEvent, entrySource: string, eventScope: EventScope) => void;
}) {
    const { sourceId: storeSourceId } = getStore();
    const isMenuOpen = isContextMenuOpen() && sourceId === storeSourceId;

    React.useEffect(() => {
        verifyEventData(onCloseContextMenu);
    });

    React.useEffect(() => {
        if (viewState) {
            openEventContextMenu(sourceId, viewState);
        }
    }, [viewState]);

    const closeContextMenu = React.useCallback(() => {
        closeEventContextMenu();
        onCloseContextMenu?.();
    }, [onCloseContextMenu]);

    useOrchestrator(
        openEventDetails,
        actionMessage => {
            if (isMenuOpen) {
                onOpenEventDetails?.(actionMessage.id);
            }
        },
        [onOpenEventDetails]
    );

    useOrchestrator(
        editEvent,
        actionMessage => {
            const { eventScope } = actionMessage;
            const calendarItem = getInitializedEvent();
            if (!calendarItem) {
                return;
            }

            const entrySource = getSourceId() + 'Edit';

            if (onEditEvent) {
                onEditEvent(calendarItem, entrySource, eventScope);
            } else {
                lazyPopoutCalendarCompose.importAndExecute(
                    calendarItem,
                    entrySource,
                    false /* eventHasUpdates */,
                    undefined /* shouldDuplicate */,
                    eventScope
                );
            }
        },
        [onEditEvent]
    );

    const calendarItem = getInitializedEvent();
    if (!isMenuOpen) {
        return null;
    }

    return (
        <CalendarItemContextMenu
            isOpen={true} // if not open, this component will render null instead
            key={calendarItem.ItemId.Id}
            item={calendarItem}
            actionSource={sourceId}
            target={viewState.clickEvent.nativeEvent}
            onEditItemCallback={editEvent}
            onDuplicateItemCallback={duplicateEvent}
            onReplyToMeeting={replyToMeeting}
            onForwardItemCallback={forwardEvent}
            onProposeNewTimeCallback={proposeNewTime}
            onDeleteItemCallback={deleteEvent}
            onCancelItemCallback={cancelMeeting}
            onResponseClickedCallback={respondToMeeting}
            onContextMenuDismiss={closeContextMenu}
        />
    );
});
